import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * The rest of Grade 4.
 *
 * `arithmetic.ts`, `fractions.ts` and `decimals.ts` teach the spine Grade 4
 * shares with the grades either side of it. These lessons are the year's own
 * work, and the year has one character running through it: arithmetic stops
 * being a set of facts and becomes a set of *methods*. A two-digit product is
 * four small products in a box, a long division is one division repeated down
 * the places, and an angle is the first quantity a student measures with an
 * instrument instead of by counting.
 */
export const GRADE_FOUR_LESSONS: Lesson[] = [
  /* ---------------------------------- multiplication and division ------- */
  lesson({
    id: 'multiplicative-comparison-01',
    unitId: 'unit-multi-digit-arithmetic',
    conceptId: 'multiplicative-comparison',
    title: 'Times as Many',
    summary: 'Two ways to compare two numbers — by how much more, and by how many times — and the words that tell them apart.',
    objectives: [
      'Tell an additive comparison apart from a multiplicative one',
      'Write "n times as many" as a multiplication',
      'Find the multiplier when both quantities are known',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Two questions that sound alike',
        blocks: [
          text('Maya has 6 stickers and Ravi has 24. There are two honest ways to compare them, and they give different numbers.'),
          list([
            'Ravi has **18 more** than Maya. That is a subtraction: 24 − 6.',
            'Ravi has **4 times as many** as Maya. That is a division: 24 ÷ 6.',
          ]),
          text('Neither is more correct than the other. But a question asks for one of them, and answering with the other is the commonest slip in this whole topic.'),
        ],
      },
      intuition: {
        title: 'Why "times as many" is multiplication',
        blocks: [
          text('Draw Maya\'s stickers as one bar. "Four times as many" means: that same bar, four times over.'),
          figure({ kind: 'tape-diagram', rows: [{ label: 'Maya', parts: 1 }, { label: 'Ravi', parts: 4 }], unitLabel: '6 stickers' }, 'One bar, then the same bar four times.'),
          math('4 \\times 6 = 24'),
          warning('"Four times as many as 6" is 24, not 10. Adding the 4 answers a question nobody asked.'),
          text('Read the other way, the same picture tells you the multiplier. How many of the short bar fit along the long one? That is a division.'),
          math('24 \\div 6 = 4'),
          insight('The bar picture is the whole idea, and it does not go away. In Grade 6 it is a ratio, in Grade 7 a proportion, and in Algebra I it is the line y = 4x. Every one of them is this diagram.'),
        ],
      },
      visual: {
        title: 'Words that signal which comparison',
        blocks: [
          table(
            ['The words', 'The comparison', 'The operation'],
            [
              ['"how many more", "how much longer"', 'additive', 'subtract'],
              ['"times as many", "times as long"', 'multiplicative', 'multiply or divide'],
              ['"twice", "three times", "half as many"', 'multiplicative', 'multiply or divide'],
              ['"more than", "fewer than"', 'additive', 'add or subtract'],
            ],
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A blue ribbon is 7 times as long as a red one. The red one is 9 cm. How long is the blue ribbon?'),
          steps([
            { label: 'The red ribbon is one unit.', latex: '9 \\text{ cm}' },
            { label: '"7 times as long" repeats that unit 7 times.', latex: '7 \\times 9' },
            { label: 'Multiply.', latex: '7 \\times 9 = 63', note: 'Not 16 — that would be "7 cm longer".' },
          ]),
          text('And backwards: a rope is 56 m long, a plank is 8 m. How many times as long is the rope?'),
          steps([
            { label: 'How many planks fit along the rope?', latex: '56 \\div 8' },
            { label: 'Divide.', latex: '56 \\div 8 = 7' },
            { label: 'Check.', latex: '7 \\times 8 = 56' },
          ]),
          tip('If your answer to a "times as many" question is close to the two numbers added, look again — you have almost certainly answered the additive question.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('multiplicative-comparison', 4)] },
      independent: { title: 'On your own', blocks: [practice('multiplicative-comparison', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('multiplicative-comparison', 3, 'Read the words before you pick an operation.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            '"How many more" is a subtraction.',
            '"How many times as many" is a multiplication or a division.',
            'Draw the smaller quantity as one bar and repeat it.',
            'Check your answer against the question you were actually asked.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'multiply-multi-digit-01',
    unitId: 'unit-multi-digit-arithmetic',
    conceptId: 'multiply-multi-digit',
    title: 'Multi-Digit Multiplication',
    summary: 'Two digits times two digits is four small multiplications in a box — and the zeros are what keep them honest.',
    objectives: [
      'Multiply a two-digit number by a multiple of ten',
      'Multiply two two-digit numbers using partial products',
      'Explain why the second partial product ends in a zero',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Nothing new, only more of it',
        blocks: [
          text('In Grade 3 you learned to split a factor: 7 × 13 became 7 × 10 plus 7 × 3. Grade 4 does the same thing to *both* factors at once.'),
          math('23 \\times 45 = (20 + 3) \\times (40 + 5)'),
          text('Splitting both sides gives four small products instead of two. Every one of them is a fact you already know, followed by some zeros.'),
        ],
      },
      intuition: {
        title: 'The box, and where the zeros come from',
        blocks: [
          text('Draw a rectangle 23 units tall and 45 units wide. Cut it once across and once down, at the place-value lines.'),
          table(
            ['×', '40', '5'],
            [
              ['20', '800', '100'],
              ['3', '120', '15'],
            ],
          ),
          text('The four pieces cover the whole rectangle exactly once, so their areas add to the product.'),
          math('800 + 100 + 120 + 15 = 1035'),
          warning('The 2 in 23 is not 2 — it is 20. That is the single most common error here: 2 × 40 = 80 is written where 800 belongs, and the answer comes out roughly ten times too small.'),
          insight('This is why the second line of the written method ends in a zero. The zero is not a rule to remember; it is the tens digit saying what it is worth.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'area-model', rows: 5, columns: 9, shadedRows: 4, shadedColumns: 8 }, 'The dark block is tens × tens. The two lighter strips are the cross products, and the small corner is ones × ones.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out 38 × 26.'),
          steps([
            { label: 'Split the second factor.', latex: '38 \\times (20 + 6)' },
            { label: 'Multiply by the tens.', latex: '38 \\times 20 = 760', note: '38 × 2 = 76, then × 10.' },
            { label: 'Multiply by the ones.', latex: '38 \\times 6 = 228' },
            { label: 'Add the partial products.', latex: '760 + 228 = 988' },
          ]),
          tip('Estimate first: 38 is nearly 40 and 26 is nearly 25, so the answer should be near 1000. An answer of 300 or of 9000 is wrong before you check a single digit.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('multiply-multi-digit', 4)] },
      independent: { title: 'On your own', blocks: [practice('multiply-multi-digit', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('multiply-multi-digit', 3, 'Find the missing partial product.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Split both factors into tens and ones.',
            'Four small products, and every one of them gets used.',
            'A tens digit is worth ten times what it looks like — that is where the zero comes from.',
            'Estimate before you calculate, so a lost zero cannot survive.',
          ]),
          insight('The box you have just drawn is the same box that multiplies (x + 3)(x + 5) in Algebra I. Meeting it now in friendly numbers is the point.'),
        ],
      },
    },
  }),

  lesson({
    id: 'long-division-01',
    unitId: 'unit-multi-digit-arithmetic',
    conceptId: 'long-division',
    title: 'Long Division',
    summary: 'One division, repeated down the places, carrying what is left over into the next.',
    objectives: [
      'Divide a three- or four-digit number by a single digit',
      'Write a zero in the quotient when a place will not divide',
      'Check a division by multiplying back',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Sharing, one place at a time',
        blocks: [
          text('You already know how to share 17 sweets between 5 children: 3 each, 2 left over. Long division is that idea applied to each place value in turn, from the left.'),
          text('Share out the hundreds. Whatever will not share, break down into tens and share those. Whatever will not share again, break into ones.'),
        ],
      },
      intuition: {
        title: 'Why it goes from the left',
        blocks: [
          text('Column addition works from the right, so starting a division from the left feels wrong. It is not. Money makes it obvious.'),
          text('Share £738 between 6 people. Start with the seven £100 notes: one each, one note left over. That leftover note is worth ten £10 notes, so the tens column now has 13 tens, not 3.'),
          steps([
            { label: 'Hundreds.', latex: '7 \\div 6 = 1 \\text{ r } 1' },
            { label: 'The leftover hundred becomes ten tens.', latex: '10 + 3 = 13 \\text{ tens}' },
            { label: 'Tens.', latex: '13 \\div 6 = 2 \\text{ r } 1' },
            { label: 'The leftover ten becomes ten ones.', latex: '10 + 8 = 18 \\text{ ones}' },
            { label: 'Ones.', latex: '18 \\div 6 = 3' },
          ]),
          math('738 \\div 6 = 123'),
          insight('Every "carry" in long division is one note being broken into ten smaller ones. Nothing is created and nothing is lost.'),
        ],
      },
      visual: {
        title: 'When a place will not divide',
        blocks: [
          text('Divide 618 by 6. The hundreds give 1. Then the tens: there is 1 ten, and 6 will not go into 1 even once.'),
          warning('That place still gets a digit, and the digit is **0**. Skipping it gives 13 instead of 103 — an answer nearly ten times too small.'),
          figure({ kind: 'place-value', digits: '103', places: ['hundreds', 'tens', 'ones'] }, 'The zero is doing real work: it holds the 1 in the hundreds column.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out 4 or 5 digits the same way. Divide 2 415 by 7.'),
          steps([
            { label: 'Thousands: 7 will not go into 2.', latex: '2 \\div 7 = 0', note: 'So the quotient has no thousands digit; carry the 2.' },
            { label: 'Hundreds.', latex: '24 \\div 7 = 3 \\text{ r } 3' },
            { label: 'Tens.', latex: '31 \\div 7 = 4 \\text{ r } 3' },
            { label: 'Ones.', latex: '35 \\div 7 = 5' },
            { label: 'Read the quotient.', latex: '2415 \\div 7 = 345' },
            { label: 'Check by multiplying back.', latex: '345 \\times 7 = 2415' },
          ]),
          tip('The check is not optional politeness. Multiplying back catches a dropped zero instantly, and a dropped zero is the error this method is most prone to.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('long-division', 4)] },
      independent: { title: 'On your own', blocks: [practice('long-division', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('long-division', 3, 'Watch what the remainder means in the situation.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Work from the left, one place at a time.',
            'What will not divide is broken down into the next place, ten for one.',
            'A place that divides zero times still takes a 0 in the quotient.',
            'The remainder is always smaller than the divisor.',
            'Check: quotient × divisor + remainder = the number you started with.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'prime-composite-01',
    unitId: 'unit-factors',
    conceptId: 'prime-composite',
    title: 'Prime and Composite Numbers',
    summary: 'Some numbers can be built out of smaller ones. The ones that cannot are the atoms of arithmetic.',
    objectives: [
      'Classify a number as prime, composite or neither',
      'Explain why 1 is neither',
      'Test a number for primality efficiently',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Rectangles you can and cannot make',
        blocks: [
          text('Take 12 tiles. You can lay them out as 1 × 12, as 2 × 6, or as 3 × 4 — three different rectangles.'),
          text('Now take 13 tiles. There is only one rectangle: 1 × 13, a single line. Try any other width and a tile is left over.'),
          text('A number that can only make the one-line rectangle is **prime**. A number with a choice is **composite**.'),
        ],
      },
      intuition: {
        title: 'Counting factors, not guessing',
        blocks: [
          text('The rectangles are really a way of listing factors. So the definition is a count.'),
          list([
            '**Prime**: exactly two different factors — 1 and the number itself.',
            '**Composite**: more than two factors.',
            '**Neither**: exactly one factor. There is only one such number, and it is 1.',
          ]),
          warning('1 is not prime. It has one factor, not two. This is not a technicality invented to be annoying — if 1 were prime, a number would have endlessly many prime factorisations (12 = 2 × 2 × 3 = 1 × 2 × 2 × 3 = …), and the whole of Grade 6 number theory would collapse.'),
          insight('2 is prime, and it is the only even prime. Every other even number has 2 as a third factor. "Odd" and "prime" are different words: 9, 15 and 21 are all odd and none of them is prime.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'array', rows: 3, columns: 4, label: '12 = 3 × 4' }, 'Twelve tiles, arranged as a rectangle with a choice of shapes.'),
          figure({ kind: 'array', rows: 1, columns: 13, label: '13 = 1 × 13' }, 'Thirteen tiles have only the one line. That is what prime looks like.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Is 51 prime?'),
          steps([
            { label: 'Even? No, so 2 is not a factor.', latex: '51' },
            { label: 'Digits add to 6, which 3 divides.', latex: '5 + 1 = 6' },
            { label: 'So 3 is a factor.', latex: '51 = 3 \\times 17' },
            { label: 'Three factors at least, so composite.', latex: '1, 3, 17, 51' },
          ]),
          tip('You never have to test past the square root. To check 51, testing 2, 3, 5 and 7 is enough — if a factor bigger than 7 existed, its partner would be smaller than 7 and you would have found it already.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('prime-composite', 4)] },
      independent: { title: 'On your own', blocks: [practice('prime-composite', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('prime-composite', 3, 'Count the primes in a run of numbers.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Prime means exactly two factors.',
            'Composite means more than two.',
            '1 is neither, and 2 is the only even prime.',
            'Odd does not mean prime — test 3, 5 and 7 before you decide.',
          ]),
          insight('These are the building blocks. In Grade 6 you will show that every whole number is a product of primes in exactly one way, and that fact is the reason simplifying a fraction always terminates.'),
        ],
      },
    },
  }),

  /* ------------------------------------------------------- fractions ---- */
  lesson({
    id: 'add-like-fractions-01',
    unitId: 'unit-fraction-operations',
    conceptId: 'add-like-fractions',
    title: 'Adding Fractions with the Same Denominator',
    summary: 'When the pieces are already the same size, adding fractions is nothing but counting them.',
    objectives: [
      'Add and subtract fractions that share a denominator',
      'Explain why the denominator does not change',
      'Break a fraction into a sum of smaller fractions',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Three eighths and two eighths',
        blocks: [
          text('Three eighths of a pizza, plus two more eighths of the same pizza. How much pizza?'),
          math('\\frac{3}{8} + \\frac{2}{8} = \\frac{5}{8}'),
          text('Five eighths. Say it out loud and the arithmetic disappears: three of a thing plus two of the same thing is five of them. The thing here happens to be "an eighth".'),
        ],
      },
      intuition: {
        title: 'Why the denominator stays put',
        blocks: [
          text('The denominator is not a number being counted. It is the *name of the unit* — it tells you how big one piece is.'),
          text('Three eighths plus two eighths is five eighths, exactly as three metres plus two metres is five metres. Nobody would answer "five sixteenths" to the metres question, and it is the same mistake.'),
          math('\\frac{3}{8} + \\frac{2}{8} = \\frac{3 + 2}{8}'),
          warning('Adding the denominators gives 5/16, which is *smaller* than what you started with. Two positive amounts cannot add to less than either of them — that answer refutes itself.'),
          insight('This is the whole reason common denominators matter later. Fractions with different denominators are quantities in different units, and you cannot add centimetres to kilograms until you fix that.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'fraction-bars', bars: [{ parts: 8, shaded: 3, label: '3/8' }, { parts: 8, shaded: 2, label: '2/8' }, { parts: 8, shaded: 5, label: '5/8' }] }, 'The pieces are the same width in every bar. Only how many are shaded changes.'),
          figure({ kind: 'number-line', from: 0, to: 1, step: 0.125, labelAs: 'fraction', marks: [{ at: 0.375, label: '3/8' }, { at: 0.625, label: '5/8' }] }, 'Adding two eighths is two hops to the right, each one eighth long.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out 5/6 + 4/6.'),
          steps([
            { label: 'The pieces already match, so count them.', latex: '5 + 4 = 9' },
            { label: 'They are still sixths.', latex: '\\frac{9}{6}' },
            { label: 'That is more than one whole.', latex: '\\frac{9}{6} = 1\\tfrac{3}{6}', note: 'Which is fine. A fraction is allowed to be bigger than 1.' },
          ]),
          text('And a subtraction: 7/10 − 3/10.'),
          steps([
            { label: 'Subtract the counts.', latex: '7 - 3 = 4' },
            { label: 'Same pieces.', latex: '\\frac{4}{10}' },
            { label: 'If asked for lowest terms, simplify.', latex: '\\frac{4}{10} = \\frac{2}{5}' },
          ]),
          tip('Breaking a fraction apart is the same move run backwards: 5/8 is 1/8 + 4/8, and also 2/8 + 3/8. That is how you will later subtract 1/8 from a whole number.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('add-like-fractions', 4)] },
      independent: { title: 'On your own', blocks: [practice('add-like-fractions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('add-like-fractions', 3, 'Simplify your answer where you can.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Same denominator means same-size pieces — so just count them.',
            'The denominator names the unit and never changes.',
            'An answer bigger than 1 is perfectly allowed.',
            'A fraction can be broken into a sum of smaller ones with the same denominator.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'fraction-times-whole-01',
    unitId: 'unit-fraction-operations',
    conceptId: 'fraction-times-whole',
    title: 'A Fraction Times a Whole Number',
    summary: 'Three lots of two fifths is six fifths — more pieces, never smaller ones.',
    objectives: [
      'Multiply a fraction by a whole number',
      'Explain the answer as repeated addition',
      'Find a fraction of a quantity',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Multiplication has not changed',
        blocks: [
          text('Multiplication has always meant "so many lots of". Three lots of 5 is 5 + 5 + 5. Three lots of 2/5 is exactly as literal:'),
          math('3 \\times \\frac{2}{5} = \\frac{2}{5} + \\frac{2}{5} + \\frac{2}{5} = \\frac{6}{5}'),
          text('You already know how to do that sum — the denominators match, so you count the pieces.'),
        ],
      },
      intuition: {
        title: 'Why only the top number moves',
        blocks: [
          text('Repeating an amount three times gives you three times as many pieces. It does not make each piece smaller.'),
          math('3 \\times \\frac{2}{5} = \\frac{3 \\times 2}{5} = \\frac{6}{5}'),
          warning('Multiplying the denominator too — 6/15 — gives you back exactly 2/5, the amount you started with. If tripling something leaves it unchanged, the method is wrong.'),
          text('The other direction is just as useful. "Three quarters of 20" means: cut 20 into 4 equal parts, then take 3 of them.'),
          steps([
            { label: 'One quarter of 20.', latex: '20 \\div 4 = 5' },
            { label: 'Three of those.', latex: '3 \\times 5 = 15' },
          ]),
          insight('Divide by the bottom, multiply by the top. That single sentence handles "of a quantity" questions all the way through percentages in Grade 6.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'fraction-bars', bars: [{ parts: 5, shaded: 2, label: '2/5' }, { parts: 5, shaded: 2, label: '2/5' }, { parts: 5, shaded: 2, label: '2/5' }] }, 'Six shaded fifths in total. Every piece is still a fifth.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Each jug holds 3/4 of a litre. How much do 5 jugs hold?'),
          steps([
            { label: 'Same amount, five times over.', latex: '5 \\times \\frac{3}{4}' },
            { label: 'Multiply the count of pieces.', latex: '\\frac{5 \\times 3}{4} = \\frac{15}{4}' },
            { label: 'Say it as a mixed number if it helps.', latex: '\\frac{15}{4} = 3\\tfrac{3}{4} \\text{ litres}' },
          ]),
          tip('Sanity check: each jug is a bit less than a litre, so five of them should be a bit less than five litres. 3¾ passes. 15/20 would not.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('fraction-times-whole', 4)] },
      independent: { title: 'On your own', blocks: [practice('fraction-times-whole', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('fraction-times-whole', 3, 'Some of these ask for a fraction of an amount.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A whole number times a fraction is repeated addition.',
            'Only the numerator is multiplied — the pieces keep their size.',
            '"A fraction of an amount": divide by the bottom, multiply by the top.',
            'Estimate first, so an answer that shrank when it should have grown is caught.',
          ]),
        ],
      },
    },
  }),

  /* -------------------------------------------------------- decimals ---- */
  lesson({
    id: 'fractions-to-decimals-01',
    unitId: 'unit-decimals',
    conceptId: 'fractions-to-decimals',
    title: 'Fractions and Decimals',
    summary: 'A decimal is a fraction whose denominator you never write down, because it is always ten or a hundred.',
    objectives: [
      'Write tenths and hundredths as decimals, and back again',
      'Rewrite tenths as hundredths in order to combine them',
      'Explain why 0.3 and 0.30 are the same number',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'The same idea, written two ways',
        blocks: [
          text('You already know that place value carries on past the ones: tenths, then hundredths. You also know what 3/10 means. These are the same fact.'),
          math('\\frac{3}{10} = 0.3 \\qquad \\frac{47}{100} = 0.47'),
          text('The decimal point is not a new invention. It is a marker saying "the whole numbers stop here, and the fractions with denominators of 10, 100, 1000 begin".'),
        ],
      },
      intuition: {
        title: 'The denominator picks the column',
        blocks: [
          text('Every digit after the point sits in a column, and the column names the denominator.'),
          table(
            ['Fraction', 'In words', 'Decimal'],
            [
              ['3/10', 'three tenths', '0.3'],
              ['3/100', 'three hundredths', '0.03'],
              ['30/100', 'thirty hundredths', '0.30'],
              ['47/100', 'forty-seven hundredths', '0.47'],
            ],
          ),
          warning('3/100 is 0.03, not 0.3. Writing the numerator straight after the point ignores the denominator entirely, and makes the answer ten times too big.'),
          text('Row three explains something that puzzles most students. 30/100 and 3/10 are equivalent fractions — cut every tenth into ten — so 0.30 and 0.3 are the same number.'),
          insight('That is why a trailing zero after the point changes nothing, while a leading zero before it changes everything.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'grid', width: 10, height: 10, shaded: [[0, 0], [0, 1], [0, 2], [0, 3], [0, 4], [0, 5], [0, 6], [1, 0], [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6]] }, 'A hundred square with 14 cells shaded: 14/100, or 0.14.'),
          figure({ kind: 'number-line', from: 0, to: 1, step: 0.1, marks: [{ at: 0.3, label: '0.3' }, { at: 0.7, label: '0.7' }] }, 'The tenths, on the line where they live.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Work out 3/10 + 27/100, as a decimal.'),
          steps([
            { label: 'The pieces are different sizes, so make them match.', latex: '\\frac{3}{10} = \\frac{30}{100}' },
            { label: 'Now they can be counted together.', latex: '\\frac{30}{100} + \\frac{27}{100} = \\frac{57}{100}' },
            { label: 'Write it as a decimal.', latex: '0.57' },
          ]),
          tip('Reading the answer aloud checks it: "fifty-seven hundredths". If what you say does not match what you wrote, one of them is wrong.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('fractions-to-decimals', 4)] },
      independent: { title: 'On your own', blocks: [practice('fractions-to-decimals', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('fractions-to-decimals', 3, 'Combine tenths with hundredths.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Tenths are one place after the point; hundredths are two.',
            'The denominator decides the column, not the size of the numerator.',
            '0.3 and 0.30 are the same number, because 3/10 and 30/100 are the same fraction.',
            'To add tenths to hundredths, rewrite the tenths as hundredths first.',
          ]),
        ],
      },
    },
  }),

  /* ----------------------------------------------------- measurement ---- */
  lesson({
    id: 'unit-conversion-01',
    unitId: 'unit-measure-geometry-4',
    conceptId: 'unit-conversion',
    title: 'Converting Units',
    summary: 'The same quantity, counted in different-sized units — and the smaller the unit, the more of them there are.',
    objectives: [
      'Convert between units of length, mass, capacity and time',
      'Decide whether a conversion multiplies or divides',
      'Rewrite a two-unit measurement as a single number',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'One quantity, many names',
        blocks: [
          text('A door is 2 metres tall. It is also 200 centimetres tall, and 2000 millimetres tall. The door has not changed; only the ruler has.'),
          text('Converting units is not a calculation about the object. It is a calculation about the unit you are counting in.'),
        ],
      },
      intuition: {
        title: 'One question decides the operation',
        blocks: [
          text('Before you multiply or divide, ask: **am I moving to a smaller unit or a bigger one?**'),
          list([
            'To a **smaller** unit, there will be **more** of them. Multiply.',
            'To a **bigger** unit, there will be **fewer** of them. Divide.',
          ]),
          text('Centimetres are smaller than metres, so 3 m becomes a bigger number of centimetres: 300. Grams are smaller than kilograms, so 4 kg is 4000 g.'),
          warning('Getting the direction wrong is not a small error. Converting 3 m to centimetres by dividing gives 0.03 — a door three centimetres tall. Ask whether the answer is a sensible size before you write it down.'),
          insight('Time is the exception that proves the rule. Metric units step by 10, 100 or 1000, but an hour is 60 minutes and a minute is 60 seconds. The *method* is identical; only the number changes.'),
        ],
      },
      visual: {
        title: 'The conversions worth knowing by heart',
        blocks: [
          table(
            ['Quantity', 'Conversion'],
            [
              ['Length', '1 km = 1000 m, 1 m = 100 cm, 1 cm = 10 mm'],
              ['Mass', '1 kg = 1000 g'],
              ['Capacity', '1 litre = 1000 ml'],
              ['Time', '1 hour = 60 minutes, 1 minute = 60 seconds, 1 day = 24 hours'],
            ],
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A rope is 4 m and 35 cm long. How long is it in centimetres?'),
          steps([
            { label: 'Convert the larger unit first.', latex: '4 \\times 100 = 400 \\text{ cm}' },
            { label: 'Add what is left over.', latex: '400 + 35 = 435 \\text{ cm}' },
          ]),
          warning('Adding 4 and 35 to get 39 treats a metre and a centimetre as the same size. They are not, and the answer is out by a factor of ten.'),
          text('And the other direction: a bottle holds 2500 ml. How many litres?'),
          steps([
            { label: 'Litres are bigger, so divide.', latex: '2500 \\div 1000' },
            { label: 'Work it out.', latex: '2.5 \\text{ litres}' },
          ]),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('unit-conversion', 4)] },
      independent: { title: 'On your own', blocks: [practice('unit-conversion', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('unit-conversion', 3, 'Some of these are given in two units at once.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Smaller unit, more of them — multiply.',
            'Bigger unit, fewer of them — divide.',
            'Learn the handful of conversion facts; the method is always the same.',
            'Check the size of your answer against the real object.',
          ]),
        ],
      },
    },
  }),

  /* -------------------------------------------------------- geometry ---- */
  lesson({
    id: 'angle-measure-01',
    unitId: 'unit-measure-geometry-4',
    conceptId: 'angle-measure',
    title: 'Measuring Angles',
    summary: 'An angle is an amount of turn, a degree is the unit it is counted in, and a protractor is the ruler.',
    objectives: [
      'Name an angle as acute, right, obtuse or straight',
      'Measure an angle with a protractor, reading the correct scale',
      'Add and subtract angles that share a vertex',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'An angle is not a shape',
        blocks: [
          text('Stand facing a door and turn until you face the window. How far did you turn? That amount is an angle, and it does not depend on how long your arms are.'),
          text('Angles are measured in **degrees**. A full turn is 360°, a half turn is 180°, and a quarter turn — the corner of a page — is 90°.'),
          warning('The length of the two arms tells you nothing about the angle. A big drawing of a 30° angle is still 30°.'),
        ],
      },
      intuition: {
        title: 'Four names before any measuring',
        blocks: [
          list([
            '**Acute**: less than 90°. Sharper than the corner of a page.',
            '**Right**: exactly 90°.',
            '**Obtuse**: between 90° and 180°. Blunter than the corner of a page.',
            '**Straight**: exactly 180°. The two arms make one line.',
          ]),
          text('Naming the angle *before* you measure is worth the two seconds it costs, because it catches the one error everybody makes with a protractor.'),
          figure({ kind: 'angle', degrees: 50, label: 'Acute: less than a right angle' }),
          figure({ kind: 'angle', degrees: 130, label: 'Obtuse: more than a right angle' }),
        ],
      },
      visual: {
        title: 'The protractor has two scales, and only one of them is yours',
        blocks: [
          text('Every protractor carries two rows of numbers running in opposite directions, so that it works whichever way round the angle is drawn. Exactly one of them applies to your angle.'),
          figure({ kind: 'angle', degrees: 50, protractor: true, labels: { angle: '50°' } }, 'Find the scale whose 0 lies along one arm, and read round from there.'),
          warning('Read the other scale and 50° becomes 130°. The two answers always add to 180, which is how you can spot the error: if you called an angle acute and then measured 130°, you read the wrong row.'),
          insight('This is why the naming step comes first. The picture tells you whether the answer should be under or over 90°, and the protractor only fills in the exact number.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A ray inside an angle splits it into two parts. The whole angle is 130°, and one part measures 40°. How big is the other?'),
          figure({ kind: 'angle', degrees: 130, split: 40 }, 'The dashed ray cuts the angle into two pieces.'),
          steps([
            { label: 'The two parts make up the whole.', latex: '40 + \\square = 130' },
            { label: 'So subtract.', latex: '130 - 40 = 90' },
            { label: 'Check that it is sensible.', latex: '90^\\circ', note: 'A right angle — and the picture agrees.' },
          ]),
          tip('Angles at a point add up exactly like lengths do. Nothing about "turn" makes the arithmetic different.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('angle-measure', 4)] },
      independent: { title: 'On your own', blocks: [practice('angle-measure', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('angle-measure', 3, 'Angles split into parts.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'An angle measures turn, not length.',
            'Acute under 90°, right at 90°, obtuse between 90° and 180°, straight at 180°.',
            'Name the angle before measuring, then use the scale that starts at 0 on one arm.',
            'Angles at a point add and subtract like ordinary numbers.',
          ]),
          insight('In Grade 7 you will meet angles you cannot measure, and have to find from relationships instead. All of that rests on being sure what a degree is.'),
        ],
      },
    },
  }),

  lesson({
    id: 'symmetry-01',
    unitId: 'unit-measure-geometry-4',
    conceptId: 'symmetry',
    title: 'Lines of Symmetry',
    summary: 'A fold that leaves a shape looking untouched — and most shapes do not have one.',
    objectives: [
      'Test a line for symmetry by folding',
      'Count the lines of symmetry of common shapes',
      'Explain why a rectangle has two and not four',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'The folding test',
        blocks: [
          text('Cut a shape out of paper and fold it along a line. If the two halves land exactly on top of each other — no overhang, no gap — that line is a **line of symmetry**.'),
          text('That is the whole definition, and it is a test you can actually perform. Everything else in this lesson is a consequence of it.'),
        ],
      },
      intuition: {
        title: 'Why a rectangle has two, not four',
        blocks: [
          text('A rectangle folds neatly down the middle from top to bottom, and neatly across the middle from side to side. Two lines of symmetry.'),
          text('It is tempting to add the two diagonals — they pass through the centre and they look symmetrical. Fold along one and see what happens: a long corner lands on a short corner, and the halves miss.'),
          figure({ kind: 'symmetry', shape: 'rectangle', showLines: true, label: 'Rectangle: two lines' }),
          warning('A line through the centre is not automatically a mirror line. The only test that counts is the fold.'),
          text('The same warning applies to counting sides. A regular polygon does have one line of symmetry per side — but "regular" is doing all the work in that sentence.'),
          figure({ kind: 'symmetry', shape: 'parallelogram', showLines: true, label: 'Parallelogram: none at all' }),
          insight('A parallelogram has four sides, two pairs of equal sides, two pairs of equal angles — and not one line of symmetry. Looking balanced and being symmetrical are different things.'),
        ],
      },
      visual: {
        title: 'Some shapes and their folds',
        blocks: [
          figure({ kind: 'symmetry', shape: 'square', showLines: true, label: 'Square: four' }),
          figure({ kind: 'symmetry', shape: 'isosceles-triangle', showLines: true, label: 'Isosceles triangle: one' }),
          figure({ kind: 'symmetry', shape: 'scalene-triangle', label: 'Scalene triangle: none' }),
          figure({ kind: 'symmetry', shape: 'regular-hexagon', showLines: true, label: 'Regular hexagon: six' }),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('How many lines of symmetry does a kite have?'),
          steps([
            { label: 'Try the fold from the top vertex to the bottom one.', latex: '\\text{fold}', note: 'The two halves match exactly.' },
            { label: 'Try the fold across, between the two side vertices.', latex: '\\text{fold}', note: 'The short end lands on the long end. It fails.' },
            { label: 'Try the other two folds, corner to corner.', latex: '\\text{fold}', note: 'They fail for the same reason.' },
            { label: 'Count the folds that worked.', latex: '1' },
          ]),
          figure({ kind: 'symmetry', shape: 'kite', showLines: true, label: 'Kite: one' }),
          tip('Work through the candidates in a fixed order — vertical, horizontal, then the diagonals — and you will not miss one or count one twice.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('symmetry', 4)] },
      independent: { title: 'On your own', blocks: [practice('symmetry', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('symmetry', 3, 'Compare shapes that look more symmetrical than they are.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A line of symmetry is a fold that makes the halves match exactly.',
            'A line through the centre is not enough — the fold has to work.',
            'A regular polygon has one line per side; an irregular one usually has none.',
            'A rectangle has two, a parallelogram has none, a square has four.',
          ]),
          insight('In Grade 8 a fold becomes a *reflection*, one of the transformations that decide when two figures are congruent. The folding test you are doing now is that idea, done with paper.'),
        ],
      },
    },
  }),
];
